import { c as _c } from "react/compiler-runtime";
import { MAX_PLANS_PER_BOOKING_LIMITS_REQUEST } from '@commutifi/models/Provider';
import { InfiniteData, QueryKey, UseInfiniteQueryOptions, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { omit } from 'lodash';
import { AccountWalletProps, AccountWalletStatus } from '@commutifi/models/AccountsWallets';
import { CardProps } from '@commutifi/models/Cards';
import { Account, GetResponse, RequestParams } from '@commutifi-fe/interfaces';
import { CommutifiError } from 'shared/interfaces';
import { ExtractFnReturnType, MutationConfig, MutationConfigPickRes, queryClient, QueryConfig, QueryConfigPickRes, QueryResult } from '../../reactQuery';
import type { BookingProps } from '../bookings';
import { WalletBalance } from '../wallets';
import { AccountBookingQueryParams, PatchAccountPasswordBody, PlanQueryParams, PlanSuggestionsQueryParams, addGroupToAccount, batchUpdateAccounts, createAccount, createGuestAccount, fetchAccountAttributes, fetchAccountBookings, fetchAccountCards, fetchAccountDefaultSurvey, fetchAccountHasCommuteData, fetchAccountKCI, fetchAccountPaymentMethods, fetchAccountRegionMetrics, fetchAccountScores, fetchAccountSubscriptions, fetchAccountSurveyAlreadyAnswered, fetchAccountWalletEnrolments, fetchAccountWalletEntries, fetchPlans, fetchPlansSuggestions, patchAccountPassword, patchAccountsGroups, postAccountPaymentMethods, fetchAccountWorksites, postEmailVerification, postEmailVerificationEmail, updateAccountAttributes, fetchBookingLimits } from './api';
import { PaymentMethod, PostPaymentMethodBody } from '../paymentMethods';
import { Primitive } from 'utility-types';
export const accountsQueryKeys = {
  getAccountPrefix: (accountId: string | undefined | null) => `accounts/${accountId}`,
  getAccountsWallets: (accountId: string | undefined | null, queryParams?: Parameters<typeof fetchAccountWalletEnrolments>[1]) => [`${accountsQueryKeys.getAccountPrefix(accountId)}/accounts-wallets`, ...(queryParams ? [queryParams] : [])] as const,
  getAccountsWalletsForHeader: (accountId: string | undefined | null) => [...accountsQueryKeys.getAccountsWallets(accountId), 'header'] as const,
  getAccountCards: (accountId: string | undefined | null, queryParams?: RequestParams) => [`${accountsQueryKeys.getAccountPrefix(accountId)}/cards`, ...(queryParams ? [queryParams] : [])] as const,
  getAccountHasCommute: (accountId: string | undefined | null) => [`${accountsQueryKeys.getAccountPrefix(accountId)}`, 'has-commute'] as const,
  getAccountDefaultSurvey: (accountId: string | undefined | null, queryParams?: RequestParams) => [`${accountsQueryKeys.getAccountPrefix(accountId)}`, 'surveys', 'default', ...(queryParams ? [queryParams] : [])] as const,
  getAccountAttributes: (accountId: string | undefined | null) => [`${accountsQueryKeys.getAccountPrefix(accountId)}/attributes`] as const,
  getAccountSurveyAlreadyAnswered: (accountId: string | undefined | null, surveyId: string | undefined) => [`${accountsQueryKeys.getAccountPrefix(accountId)}`, 'surveys', surveyId, 'already-answered'] as const,
  getAccountPaymentMethods: (accountId: string | undefined | null, queryParams?: RequestParams) => ['paymentMethods', accountId, ...(queryParams ? [queryParams] : [])] as const,
  // Nested so one account's answers — whatever plans they were asked for — are invalidated by
  // naming the account, and every account's by a caller that cannot name one.
  allBookingLimits: () => ['booking-limits'] as const,
  getBookingLimits: (accountId: string | undefined | null) => [...accountsQueryKeys.allBookingLimits(), accountId] as const,
  getBookingLimitsForPlans: (accountId: string | undefined | null, planIds: string[]) => [...accountsQueryKeys.getBookingLimits(accountId), planIds] as const,
  getAccountWorksites: (accountId: string | null | undefined, queryParams?: Parameters<typeof fetchAccountWorksites>[1]) => [`${accountsQueryKeys.getAccountPrefix(accountId)}/worksites`, ...(queryParams ? [queryParams] : [])] as const
};

/**
 * Mutation keys are kept apart from `accountsQueryKeys`: queries and mutations live in
 * separate caches, so a mutation key passed to `invalidateQueries` matches nothing.
 * Both password mutations share a parent with a leaf each so they stay individually
 * addressable — mutation filters partial-match by default.
 */
export const accountsMutationKeys = {
  accountPassword: (accountId: string) => [accountsQueryKeys.getAccountPrefix(accountId), 'password'] as const,
  patchAccountPassword: (accountId: string) => [...accountsMutationKeys.accountPassword(accountId), 'change'] as const,
  setInitialAccountPassword: (accountId: string) => [...accountsMutationKeys.accountPassword(accountId), 'initial'] as const
};
export const usePostEmailVerificationEmail = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const usePostEmailVerification = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp2,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useCreateAccount = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["accounts"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationFn: _temp3,
      mutationKey: t0,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useCreateGuestAccount = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["accounts/guests"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationFn: _temp4,
      mutationKey: t0,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useAddGroupToAccount = (groupId, options) => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== groupId) {
    t0 = accountId => addGroupToAccount(groupId, accountId);
    t1 = ["accounts/groups", groupId];
    $[0] = groupId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: t0,
      mutationKey: t1,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
};
export const useBatchUpdateAccounts = (accountIds, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== accountIds) {
    t0 = body => {
      if (!body) {
        throw new Error("Missing account ids to update, or the data to patch");
      }
      return batchUpdateAccounts(accountIds, body);
    };
    $[0] = accountIds;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export function useLegacySubscriptions(accountId, options) {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== accountId) {
    t0 = ["/_/accounts", accountId, "subscriptions"];
    t1 = async () => {
      if (!accountId) {
        throw Error("Missing account id");
      }
      return fetchAccountSubscriptions(accountId);
    };
    $[0] = accountId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
}
const planListRequiredFields = ['id', 'name', 'shortDescription', 'canBuyMany', 'costType', 'costAmount', 'isFeatured', 'frequency', 'bookingType', 'imageUrl', 'regions.id', 'regions.point', 'service.id', 'service.mode', 'service.category', 'thirdPartyIntegration', 'merchant.id', 'merchant.currency', 'merchant.termsOfService', 'merchant.termsOfServiceLink'];
const planListRequiredRelation = ['regions', 'service', 'merchant'];
export const usePlans = (accountId, queryParams, options) => {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== accountId || $[1] !== queryParams) {
    t0 = ["plans", accountId, queryParams];
    t1 = async () => {
      const {
        records
      } = await fetchPlans(accountId, {
        ...queryParams,
        fields: planListRequiredFields,
        relations: planListRequiredRelation
      });
      return records;
    };
    $[0] = accountId;
    $[1] = queryParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
};
export const usePlansSuggestions = (accountId, t0, options) => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== accountId || $[3] !== queryParams) {
    t2 = ["plans/suggestions", accountId, queryParams];
    t3 = async () => {
      const {
        records
      } = await fetchPlansSuggestions(accountId, queryParams);
      return records;
    };
    $[2] = accountId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
};
export const accountBookingsListDefaultQuery = {
  relations: ['plan', 'plan.service', 'plan.regions'],
  fields: ['id', 'createdAt', 'startDate', 'endDate', 'bookingType', 'timezone', 'quantity', 'plan.id', 'plan.name', 'plan.imageUrl', 'plan.thirdPartyIntegration', 'plan.regions.id', 'plan.regions.point', 'plan.service.id', 'plan.service.mode', 'plan.service.category']
};

/**
 * Identifier of useAccountBookings query hook
 * @param accountId - Account id
 * @returns : React Query key excluding specific parameters like the queryParams
 */
export function useAccountBookingsInfinite(accountId, queryParams, options) {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== accountId || $[1] !== queryParams) {
    t0 = ["accounts", accountId, "bookings", queryParams];
    t1 = async t2 => {
      const {
        pageParam: t3
      } = t2;
      const pageParam = t3 === undefined ? 0 : t3;
      if (!accountId) {
        throw Error("Missing Account id");
      }
      return fetchAccountBookings(accountId, {
        ...accountBookingsListDefaultQuery,
        ...queryParams,
        offset: pageParam
      });
    };
    $[0] = accountId;
    $[1] = queryParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useInfiniteQuery(t2);
}
export function useAccountBookings(accountId, queryParams, options) {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== accountId || $[1] !== queryParams) {
    t0 = ["accounts", accountId, "bookings", queryParams];
    t1 = async () => {
      if (!accountId) {
        throw Error("Missing Account id");
      }
      const {
        records
      } = await fetchAccountBookings(accountId, {
        ...accountBookingsListDefaultQuery,
        ...queryParams
      });
      return records;
    };
    $[0] = accountId;
    $[1] = queryParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
}
export const useAccountEnabledWalletEnrolmentsByWalletId = (accountId: string, queryParams: Parameters<typeof fetchAccountWalletEnrolments>[1] = {}, options?: QueryConfigPickRes<typeof fetchAccountWalletEnrolments, 'records', Record<string, AccountWalletProps & WalletBalance>>): QueryResult<Record<string, AccountWalletProps & WalletBalance>> => {
  const defaultQueryParams = {
    fields: ['id', 'status', 'walletId', 'wallet.type'],
    relations: ['wallet']
  };
  const params = {
    ...defaultQueryParams,
    ...queryParams,
    status: [AccountWalletStatus.Active, AccountWalletStatus.Processing]
  };
  return useQuery({
    queryKey: [`accounts/${accountId}/accounts-wallets`, params],
    queryFn: async () => {
      if (!accountId) {
        throw Error('Missing account id');
      }
      const {
        records
      } = await fetchAccountWalletEnrolments(accountId, params);
      return records;
    },
    ...options,
    select: enrolments => enrolments.reduce<Record<string, AccountWalletProps & WalletBalance>>((byWalletId, enrolment) => ({
      ...byWalletId,
      [enrolment.walletId || '']: enrolment
    }), {})
  });
};
export const useAccountWalletEnrolments = (accountId, t0, options) => {
  const $ = _c(13);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  if ($[2] !== accountId || $[3] !== options?.queryKey || $[4] !== queryParams) {
    t2 = options?.queryKey || ["accounts", accountId, "accounts-wallets", queryParams];
    $[2] = accountId;
    $[3] = options?.queryKey;
    $[4] = queryParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== accountId || $[7] !== queryParams) {
    t3 = async () => {
      if (!accountId) {
        throw Error("Missing account id");
      }
      return fetchAccountWalletEnrolments(accountId, queryParams);
    };
    $[6] = accountId;
    $[7] = queryParams;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== options || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      select: _temp5,
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[9] = options;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return useQuery(t4);
};
export const useAccountWalletEnrolmentsForHeader = (accountId, t0, options) => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["id"];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== queryParams) {
    t3 = {
      fields: t2,
      ...queryParams
    };
    $[3] = queryParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== accountId) {
    t4 = accountsQueryKeys.getAccountsWalletsForHeader(accountId);
    $[5] = accountId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== options || $[8] !== t4) {
    t5 = {
      ...options,
      queryKey: t4
    };
    $[7] = options;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return useAccountWalletEnrolments(accountId, t3, t5);
};
export const useWalletTransactions = (accountId, queryParams, options) => {
  const $ = _c(12);
  const t0 = `accounts/${accountId}/wallet-entries`;
  let t1;
  if ($[0] !== queryParams) {
    t1 = omit(queryParams, ["fields", "relations"]);
    $[0] = queryParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = [t0, t1];
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== accountId || $[6] !== queryParams) {
    t3 = async () => {
      if (!accountId) {
        throw Error("Cannot fetch account wallet entries without an accountId parameter");
      }
      return fetchAccountWalletEntries(accountId, queryParams);
    };
    $[5] = accountId;
    $[6] = queryParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== options || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[8] = options;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useQuery(t4);
};
export function useAccountCards(accountId, t0, options) {
  const $ = _c(13);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  if ($[2] !== accountId || $[3] !== queryParams) {
    t2 = accountsQueryKeys.getAccountCards(accountId, queryParams);
    $[2] = accountId;
    $[3] = queryParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== accountId || $[6] !== queryParams) {
    t3 = async () => {
      if (!accountId) {
        throw Error("Cannot fetch account cards without an accountId parameter");
      }
      const {
        records
      } = await fetchAccountCards(accountId, queryParams);
      return records;
    };
    $[5] = accountId;
    $[6] = queryParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = Boolean(accountId);
  let t5;
  if ($[8] !== options || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      ...options
    };
    $[8] = options;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return useQuery(t5);
}
export function useAccountCardsMutation(accountId, options) {
  const $ = _c(5);
  let t0;
  if ($[0] !== accountId) {
    t0 = async queryParams => {
      if (!accountId) {
        throw Error("Cannot fetch account cards without an accountId parameter");
      }
      const {
        records
      } = await fetchAccountCards(accountId, queryParams);
      return records;
    };
    $[0] = accountId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
}
export const usePatchAccountsGroups = (enterpriseId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = body => {
      body.forEach(_temp6);
      return patchAccountsGroups(enterpriseId, body);
    };
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useAccountKCI = (accountId, t0, options) => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== accountId || $[3] !== queryParams) {
    t2 = ["accounts", accountId, "kci", queryParams];
    t3 = async () => fetchAccountKCI(accountId, queryParams);
    $[2] = accountId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
};
export const useAccountRegionMetrics = (accountId, t0, options) => {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== accountId || $[3] !== queryParams) {
    t2 = ["accounts", accountId, "region-metrics", queryParams];
    t3 = async () => {
      if (!accountId) {
        throw new Error("Missing account id");
      }
      return fetchAccountRegionMetrics(accountId, queryParams);
    };
    $[2] = accountId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  const t4 = Boolean(accountId);
  let t5;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: 60000,
      retryOnMount: false,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useQuery(t5);
};
export const useAccountScores = (accountId, t0, options) => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== accountId || $[3] !== queryParams) {
    t2 = ["accounts", accountId, "scores/variation-graph", queryParams];
    t3 = async () => fetchAccountScores(accountId, queryParams);
    $[2] = accountId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
};
export const useAccountPaymentMethods = (accountId, t0, options) => {
  const $ = _c(19);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  if ($[2] !== accountId) {
    t2 = paymentMethod => {
      queryClient.setQueryData(accountsQueryKeys.getAccountPaymentMethods(accountId), old => old ? [...old, paymentMethod] : [paymentMethod]);
    };
    $[2] = accountId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const optimisticUpdate = t2;
  let t3;
  if ($[4] !== accountId || $[5] !== queryParams) {
    t3 = accountsQueryKeys.getAccountPaymentMethods(accountId, queryParams);
    $[4] = accountId;
    $[5] = queryParams;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== accountId || $[8] !== queryParams) {
    t4 = async () => {
      if (!accountId) {
        throw new Error("Missing account id");
      }
      const {
        records
      } = await fetchAccountPaymentMethods(accountId, queryParams);
      return records;
    };
    $[7] = accountId;
    $[8] = queryParams;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== options || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      ...options
    };
    $[10] = options;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const queryResult = useQuery(t5);
  let t6;
  if ($[14] !== optimisticUpdate) {
    t6 = {
      optimisticUpdate
    };
    $[14] = optimisticUpdate;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== queryResult || $[17] !== t6) {
    t7 = [queryResult, t6];
    $[16] = queryResult;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7 as const;
};
export const usePostAccountPaymentMethod = (accountId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== accountId) {
    t0 = body => {
      if (!accountId) {
        throw new Error("Missing account id");
      }
      return postAccountPaymentMethods(accountId, body);
    };
    $[0] = accountId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useAccountHasCommute = (accountId, options) => {
  const $ = _c(9);
  let t0;
  if ($[0] !== accountId) {
    t0 = accountsQueryKeys.getAccountHasCommute(accountId);
    $[0] = accountId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== accountId) {
    t1 = async () => {
      if (!accountId) {
        throw new Error("Missing account id");
      }
      return fetchAccountHasCommuteData(accountId);
    };
    $[2] = accountId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(accountId);
  let t3;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 30000,
      gcTime: 60000,
      retryOnMount: false,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};
export const useAccountHasCommuteDataMutation = (accountId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== accountId) {
    t0 = async () => fetchAccountHasCommuteData(accountId);
    $[0] = accountId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useAccountSurveyAlreadyAnsweredMutation = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp7,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useAccountSurveyAlreadyAnsweredQuery = (accountId, surveyId) => {
  const $ = _c(10);
  let t0;
  if ($[0] !== accountId || $[1] !== surveyId) {
    t0 = accountsQueryKeys.getAccountSurveyAlreadyAnswered(accountId, surveyId);
    $[0] = accountId;
    $[1] = surveyId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== accountId || $[4] !== surveyId) {
    t1 = async () => {
      if (!accountId || !surveyId) {
        throw new Error("Missing account id or survey id");
      }
      return fetchAccountSurveyAlreadyAnswered(accountId, surveyId);
    };
    $[3] = accountId;
    $[4] = surveyId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!accountId && !!surveyId;
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      retryOnMount: false
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
};
export const useAccountDefaultSurvey = (accountId, options) => {
  const $ = _c(9);
  let t0;
  if ($[0] !== accountId) {
    t0 = accountsQueryKeys.getAccountDefaultSurvey(accountId);
    $[0] = accountId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== accountId) {
    t1 = async () => {
      if (!accountId) {
        throw new Error("Missing account id");
      }
      return fetchAccountDefaultSurvey(accountId);
    };
    $[2] = accountId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(accountId);
  let t3;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      retryOnMount: false,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};

/**
 * Hook accepts string | undefined | null for accountId to support conditional rendering
 * and loading states. Query is automatically disabled when accountId is nullish via `enabled`.
 */
export const useAccountAttributes = (accountId, options) => {
  const $ = _c(9);
  let t0;
  if ($[0] !== accountId) {
    t0 = accountsQueryKeys.getAccountAttributes(accountId);
    $[0] = accountId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== accountId) {
    t1 = async () => {
      if (!accountId) {
        throw new Error("Missing accountId to fetch account attributes");
      }
      return fetchAccountAttributes(accountId);
    };
    $[2] = accountId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!accountId;
  let t3;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};

/**
 * Changes the password of an account that already has one. `oldPassword` is required
 * here even though the API accepts it as optional, because omitting it is only valid
 * when the account has no password yet — see `useSetInitialAccountPassword`.
 */
export const usePatchAccountPassword = (accountId, options) => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== accountId) {
    t0 = data => patchAccountPassword({
      id: accountId,
      data
    });
    t1 = accountsMutationKeys.patchAccountPassword(accountId);
    $[0] = accountId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: t0,
      mutationKey: t1,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
};

/**
 * Sets the FIRST password on an account that has none — the onboarding screen a
 * passwordless user reaches after signing in with OTP or a magic link. The signed-in
 * session is the proof of ownership, so there is no `oldPassword` to send; api-user
 * refuses this shape against an account that already has a password.
 */
export const useSetInitialAccountPassword = (accountId, options) => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== accountId) {
    t0 = data => patchAccountPassword({
      id: accountId,
      data
    });
    t1 = accountsMutationKeys.setInitialAccountPassword(accountId);
    $[0] = accountId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: t0,
      mutationKey: t1,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useMutation(t2);
};
export const useUpdateAccountAttributes = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp8,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
const BOOKING_LIMIT_STALE_TIME_MS = 5 * 60 * 1000; // 0h 5m 0s
const BOOKING_LIMIT_GC_TIME_MS = 15 * 60 * 1000; // 0h 15m 0s

/**
 * Where the account stands for each of the given plans, in one request.
 *
 * A page asks once for the plans it shows — a commute section for its cards, a plan page for its
 * one plan — rather than each Book button asking for itself. A plan absent from the answer is not
 * blocked.
 *
 * Plans beyond the server's cap are left unasked: a section showing more than that many plans
 * needs pagination before it needs this, so the cap is an assumption rather than a case handled.
 */
export const useBookingLimits = (accountId, planIds) => {
  const $ = _c(12);
  let requestedPlanIds;
  let t0;
  let t1;
  if ($[0] !== accountId || $[1] !== planIds) {
    requestedPlanIds = [...new Set(planIds.filter(_temp9))].sort().slice(0, MAX_PLANS_PER_BOOKING_LIMITS_REQUEST);
    t1 = useQuery;
    t0 = accountsQueryKeys.getBookingLimitsForPlans(accountId, requestedPlanIds);
    $[0] = accountId;
    $[1] = planIds;
    $[2] = requestedPlanIds;
    $[3] = t0;
    $[4] = t1;
  } else {
    requestedPlanIds = $[2];
    t0 = $[3];
    t1 = $[4];
  }
  let t2;
  if ($[5] !== accountId || $[6] !== requestedPlanIds) {
    t2 = async () => {
      if (!accountId) {
        throw new Error("Missing accountId to fetch booking limits");
      }
      return fetchBookingLimits(accountId, requestedPlanIds);
    };
    $[5] = accountId;
    $[6] = requestedPlanIds;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = !!accountId && requestedPlanIds.length > 0;
  let t4;
  if ($[8] !== t0 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t2,
      staleTime: BOOKING_LIMIT_STALE_TIME_MS,
      gcTime: BOOKING_LIMIT_GC_TIME_MS,
      enabled: t3
    };
    $[8] = t0;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t1(t4);
};
export const useAccountWorksites = (accountId, t0, options) => {
  const $ = _c(13);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  if ($[2] !== accountId || $[3] !== queryParams) {
    t2 = accountsQueryKeys.getAccountWorksites(accountId, queryParams);
    $[2] = accountId;
    $[3] = queryParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== accountId || $[6] !== queryParams) {
    t3 = async () => {
      if (!accountId) {
        throw new Error("Missing accountId to fetch account worksites");
      }
      return fetchAccountWorksites(accountId, queryParams);
    };
    $[5] = accountId;
    $[6] = queryParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = !!accountId;
  let t5;
  if ($[8] !== options || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      ...options
    };
    $[8] = options;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return useQuery(t5);
};
function _temp(t0) {
  const {
    accountId,
    email
  } = t0;
  if (!accountId && !email) {
    throw new Error("Missing account information to send verification Email");
  }
  return postEmailVerificationEmail({
    accountId,
    email
  });
}
function _temp2(t0) {
  const {
    accountId,
    emailVerificationToken
  } = t0;
  if (!accountId || !emailVerificationToken) {
    throw new Error("Missing account information to verify email");
  }
  return postEmailVerification(accountId, {
    emailVerificationToken
  });
}
function _temp3(accountPayload) {
  return createAccount(accountPayload);
}
function _temp4(body) {
  return createGuestAccount(body);
}
function _temp5(res) {
  return res.records as Res;
}
function _temp6(group) {
  if (!group.groupId || !group.accountIds) {
    throw new Error("Missing account ids, group id or enterprise id");
  }
  if (!group.operation) {
    throw new Error("Missing operation or operation type not found");
  }
}
async function _temp7(t0) {
  const {
    accountId,
    surveyId
  } = t0;
  return fetchAccountSurveyAlreadyAnswered(accountId, surveyId);
}
async function _temp8(t0) {
  const {
    accountId,
    attributes
  } = t0;
  if (!accountId) {
    throw new Error("Missing accountId to update account attributes");
  }
  return updateAccountAttributes(accountId, {
    attributes
  });
}
function _temp9(planId) {
  return !!planId;
}